import { useState } from "react";
import { AlertTriangle, RefreshCw, Search, Sprout, Warehouse } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useGreenhouseLayout } from "../hooks/useGreenhouses";
import type { GreenhouseLayoutBed } from "../services/greenhousesApi";

const number = new Intl.NumberFormat();

const statusClass: Record<GreenhouseLayoutBed["status"], string> = {
  Empty: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  Occupied: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200",
};

const trayTypeLabel = (trayType?: string, stockUnit?: string) => {
  if (trayType === "FourFortyTwoHole") return "442-hole";
  if (trayType === "TwoTenHole") return "210-hole";
  return trayType || stockUnit || "Other";
};

const trayBreakdown = (stock: GreenhouseLayoutBed["stock"]) => {
  const totals = new Map<string, number>();
  for (const s of stock) {
    const key = trayTypeLabel(s.trayType, s.stockUnit);
    totals.set(key, (totals.get(key) ?? 0) + s.placementQuantity);
  }
  return Array.from(totals.entries());
};

export const GreenhouseLayoutPage = () => {
  const [greenhouseId, setGreenhouseId] = useState("");
  const [search, setSearch] = useState("");
  const [occupancy, setOccupancy] = useState("all");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const debouncedSearch = useDebounce(search, 350);
  const query = useGreenhouseLayout({
    greenhouseId: greenhouseId || undefined,
    search: debouncedSearch || undefined,
    status: occupancy === "all" ? undefined : occupancy,
    pageNumber,
    pageSize,
  });

  if (query.isLoading) return <LoadingAnimation fullScreen />;
  if (query.isError || !query.data) {
    return <Card><CardContent className="p-6 text-sm text-destructive">Unable to load the greenhouse layout.</CardContent></Card>;
  }

  const data = query.data;
  const greenhouses = data.greenhouses;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-muted-foreground">Growing operations</p>
          <h1 className="text-3xl font-bold tracking-tight">Greenhouse Layout</h1>
          <p className="mt-1 text-sm text-muted-foreground">Physical stock by greenhouse, bay and bed.</p>
        </div>
        <Button variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Summary title="Greenhouses / beds" value={`${data.totalGreenhouses} / ${data.totalBeds}`} icon={<Warehouse />} />
        <Summary title="Trays on beds" value={`${number.format(data.calculatedOccupancy)} trays`} icon={<Sprout />} />
        <Summary title="Reconciliation" value={`${data.warningCount} warnings`} icon={<AlertTriangle />} />
      </div>

      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-3">
          <label className="relative md:col-span-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" value={search} onChange={(e) => { setSearch(e.target.value); setPageNumber(1); }} placeholder="Search crop, variety, bed or instruction" />
          </label>
          <select className="h-9 rounded-md border bg-background px-3 text-sm" value={greenhouseId} onChange={(e) => { setGreenhouseId(e.target.value); setPageNumber(1); }}>
            <option value="">All greenhouses</option>
            {data.greenhouseOptions.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.code})</option>)}
          </select>
          <select className="h-9 rounded-md border bg-background px-3 text-sm" value={occupancy} onChange={(e) => { setOccupancy(e.target.value); setPageNumber(1); }}>
            <option value="all">All bed states</option>
            <option value="empty">Empty</option><option value="occupied">Occupied</option>
          </select>
        </CardContent>
      </Card>

      {greenhouses.map((greenhouse) => (
        <Card key={greenhouse.id}>
          <CardHeader className="border-b pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><CardTitle>{greenhouse.name} <span className="text-sm font-normal text-muted-foreground">{greenhouse.code}</span></CardTitle><p className="mt-1 text-xs text-muted-foreground">{greenhouse.location || "Location not specified"}</p></div>
              <div className="flex gap-2 text-sm"><Badge variant="outline">{greenhouse.occupancy} trays</Badge>{greenhouse.warningCount > 0 && <Badge variant="destructive">{greenhouse.warningCount} warnings</Badge>}</div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 p-4">
            {greenhouse.bays.map((bay) => (
              <details key={bay.id} open className="rounded-lg border bg-muted/20">
                <summary className="cursor-pointer px-4 py-3 font-medium">{bay.name} <span className="ml-2 text-xs font-normal text-muted-foreground">{bay.occupancy} trays · {bay.beds.length} beds</span></summary>
                <div className="grid gap-3 border-t p-3 lg:grid-cols-2 2xl:grid-cols-3">
                  {bay.beds.map((bed) => <BedCard key={bed.id} bed={bed} />)}
                </div>
              </details>
            ))}
          </CardContent>
        </Card>
      ))}

      {greenhouses.length === 0 && <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">No beds match the selected filters.</CardContent></Card>}
      <PaginationBar
        currentPage={data.pageNumber}
        pageSize={data.pageSize}
        totalCount={data.totalCount}
        totalPages={data.totalPages}
        onPageChange={setPageNumber}
        onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
      />
      <p className="text-xs text-muted-foreground">As of {new Date(data.asOf).toLocaleString()}. Placement totals are shown as physical occupancy.</p>
    </div>
  );
};

const Summary = ({ title, value, icon, warning }: { title: string; value: string; icon: React.ReactNode; warning?: number }) => (
  <Card><CardContent className="flex items-center justify-between p-4"><div><p className="text-xs text-muted-foreground">{title}</p><p className="mt-1 text-2xl font-semibold">{value}</p>{warning ? <p className="mt-1 text-xs text-destructive">{warning} reconciliation warning(s)</p> : null}</div><span className="[&>svg]:h-6 [&>svg]:w-6 text-muted-foreground">{icon}</span></CardContent></Card>
);

const BedCard = ({ bed }: { bed: GreenhouseLayoutBed }) => (
  <div className={`rounded-lg border p-3 ${statusClass[bed.status]}`}>
    <div className="flex items-start justify-between gap-2"><div><p className="font-semibold">Bed {bed.number}</p><p className="text-xs opacity-80">{bed.calculatedOccupancy} trays</p></div><Badge variant="outline" className="bg-background/60">{bed.status}</Badge></div>
    {bed.stock.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1">
        {trayBreakdown(bed.stock).map(([label, trays]) => (
          <Badge key={label} variant="secondary" className="text-[11px]">{trays} × {label}</Badge>
        ))}
      </div>
    )}
    {bed.warnings.length > 0 && <div className="mt-3 rounded-md bg-destructive/10 p-2 text-xs text-destructive">{bed.warnings.map((warning) => <p key={warning} className="flex gap-1"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{warning}</p>)}</div>}
    <div className="mt-3 space-y-2">
      {bed.stock.map((stock) => <div key={stock.stockEntryId} className="rounded-md border bg-background/80 p-2 text-xs text-foreground"><div className="flex justify-between gap-2"><strong>{stock.cropType}{stock.variety ? ` · ${stock.variety}` : ""}</strong><span>{stock.placementQuantity} trays</span></div><div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground"><span>{stock.instructionNumber || "Standalone stock"}</span><span>{trayTypeLabel(stock.trayType, stock.stockUnit)}</span><span>{stock.reservedQuantity} reserved</span><span>{stock.availableQuantity} available</span>{stock.isReadyForDispatch && <span className="text-emerald-700">Ready</span>}</div></div>)}
      {bed.stock.length === 0 && <p className="text-xs opacity-70">No physical stock placement.</p>}
    </div>
  </div>
);
